import { useState, useMemo, useEffect } from "react";
import {
  Layout,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Drawer,
  Popconfirm,
  Badge,
  Divider,
  Tooltip,
  message,
  Empty,
  Breadcrumb,
  Avatar,
  Alert,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  HomeOutlined,
  BarChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ShoppingOutlined,
  BulbOutlined,
  WifiOutlined,
  ToolOutlined,
  UserOutlined,
  CarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { GeneralService } from "../../Services/GeneralService";
import { formatDate } from "../../components/Customs/dateFormatter";
import CreateVendorModal from "./CreateVendorModal";
import { ExpenseService } from "../../Services/ExpenseService";
import { notify } from "../../components/Customs/NotifyProvider";
import Title from "antd/es/typography/Title";

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../../../utils/permissionUtils";

const MODULE_KEY = "expense_tracker";
dayjs.extend(isBetween);

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_ICONS = {
  Electricity: <BulbOutlined />,
  Water: <WifiOutlined />,
  Maintenance: <ToolOutlined />,
  Staff: <UserOutlined />,
  Groceries: <ShoppingOutlined />,
  Transport: <CarOutlined />,
  Miscellaneous: <AppstoreOutlined />,
};

const CATEGORY_COLORS = {
  Electricity: "#faad14",
  Water: "#1677ff",
  Maintenance: "#ff4d4f",
  Staff: "#722ed1",
  Groceries: "#52c41a",
  Transport: "#13c2c2",
  Miscellaneous: "#fa8c16",
};

export default function ExpensePage() {
  // const [expenses, setExpenses] = useState(DUMMY_DATA);
  const [expenses, setExpenses] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    category: "all",
    dateRange: null,
  });
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState([]);
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItemIdx, setEditItemIdx] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [cat, setCat] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState([]);
  const [selectedCate, setSelectedCate] = useState([]);
  const [electedExpData, setSelectedExpData] = useState([]);
  const [isStock, setIsStock] = useState(false);

  // Stats
  const stats = useMemo(() => {
    const total = expenses.reduce((s, e) => s + e.total_amount, 0);

    const paid = expenses
      .filter((e) => e.status === "paid")
      .reduce((s, e) => s + e.total_amount, 0);

    const unpaid = expenses
      .filter((e) => e.status === "unpaid")
      .reduce((s, e) => s + e.total_amount, 0);

    return { total, paid, unpaid, count: expenses.length };
  }, [expenses]);

  console.log("filters.category", filters.category);

  // Filtered data
  const filteredExpenses = useMemo(() => {
    return expenses
      .filter((exp) => {
        const matchSearch =
          !filters.search ||
          (exp.bill_no || "")
            .toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          (exp.vendor_name || "")
            .toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          (exp.description || "")
            .toLowerCase()
            .includes(filters.search.toLowerCase());
        const matchStatus =
          filters.status === "all" || exp.status === filters.status;
        // const matchCategory = filters.category === "all" || exp.category === filters.category;
        const matchCategory =
          filters.category === "all" || exp.category_name === filters.category;
        const matchDate =
          !filters.dateRange ||
          dayjs(exp.expense_date).isBetween(
            filters.dateRange[0],
            filters.dateRange[1],
            "day",
            "[]",
          );
        return matchSearch && matchStatus && matchCategory && matchDate;
      })
      .sort(
        (a, b) =>
          dayjs(b.expense_date).valueOf() - dayjs(a.expense_date).valueOf(),
      );
  }, [expenses, filters]);

  // Group by expense_date
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredExpenses.forEach((exp) => {
      if (!groups[exp.expense_date]) groups[exp.expense_date] = [];
      groups[exp.expense_date].push(exp);
    });
    return groups;
  }, [filteredExpenses]);

  useEffect(() => {
    getAllExpense();
    getAllVendor();
    getAllExpeseData();
  }, []);

  const getAllExpeseData = async () => {
    try {
      const { data } = await ExpenseService.getAllExpense();

      const formatted = (data || []).map((exp) => ({
        ...exp,
        total_amount: Number(exp.total_amount) || 0,
        items: (exp.items || []).map((item) => ({
          ...item,
          qty: Number(item.qty) || 0,
          rate: Number(item.rate) || 0,
          amount: Number(item.amount) || 0,
        })),
      }));

      setExpenses(formatted);
    } catch (err) {
      console.error("Failed to load expenses:", err);
      setExpenses([]);
    }
  };

  const getAllExpense = async () => {
    try {
      const response = await GeneralService.get("expense_categories");
      if (response.status) {
        const format = response.data.map((u) => ({
          label: u.name,
          value: u.id,
        }));
        setCat(format);
      } else {
        console.error("Failed to load Expenses Category: " + response.message);
      }
    } catch (error) {
      console.error("Error loading Expenses Category: " + error.message);
    }
  };

  const getAllVendor = async () => {
    try {
      const req = await GeneralService.get("vendors");
      const format = req.data.map((u) => ({
        label: u.name,
        value: u.id,
        ...u,
      }));

      setVendors(format);
    } catch (error) {
      console.error(error);
    }
  };

  const openCreate = () => {
    setEditingExpense(null);
    setItems([]);
    form.resetFields();
    form.setFieldsValue({ expense_date: dayjs(), status: "unpaid" });
    setDrawerOpen(true);
  };

  const openEdit = (exp) => {
    setEditingExpense(exp);
    console.log("exp", exp);

    setSelectedExpData(exp);

    setItems(exp.items || []);
    form.setFieldsValue({
      ...exp,
      expense_date: dayjs(exp.expense_date),
      paid_date: exp.paid_date ? dayjs(exp.paid_date) : null,
    });
    setDrawerOpen(true);
  };

  const handleDelete = async (id) => {

    try {
      const req = await ExpenseService.deleteExpense(id)

      if (req.status) {
        notify.success(req.message)
        setExpenses((prev) => prev.filter((e) => e.id !== id));

      } else {
        notify.error(req.message)
      }
    } catch (error) {
      console.error(error);

    }
    console.log(id);

  };

  const handleStatusToggle = (id) => {
    setExpenses((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const newStatus = e.status === "paid" ? "unpaid" : "paid";
        return {
          ...e,
          status: newStatus,
          paid_date: newStatus === "paid" ? dayjs().format("YYYY-MM-DD") : null,
        };
      }),
    );
    messageApi.success("Status updated");
  };

  const handleDeleteItem = async (row, idx) => {
    console.log("row, idx", row.id, idx);

    try {

      if (!row.id) {
        setItems((prev) => prev.filter((_, i) => i !== idx));
        return notify.success("Deleted!")
      }

      const req = await ExpenseService.deleteExpenseItem(row.id);

      if (req.status) {
        notify.success(req.message)
        setItems((prev) => prev.filter((_, i) => i !== idx));
      } else {
        notify.error(req.message)

      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // console.log('values',values);


      const expenseData = {
        ...values,
        vendor_name: selectedVendor.label,
        category_name: selectedCate.label,

        expense_date: values.expense_date
          ? values.expense_date.format("YYYY-MM-DD")
          : null,
        paid_date: values.paid_date
          ? values.paid_date.format("YYYY-MM-DD")
          : null,
        paid_by: values.paid_by,
        items: items,
        total_amount:
          items.reduce((s, i) => s + Number(i.amount || 0), 0) ||
          values.amount ||
          0,
        created_at: formatDate(),
        updated_at: formatDate(),
      };

      if (editingExpense) {
        // Update API (if you have one)
        // await ExpenseService.updateExpense(editingExpense.id, expenseData);

        console.log("electedExpData", electedExpData);

        const editVal = {
          ...expenseData,
          bill_no: editingExpense.bill_no,
          id: editingExpense.id,
        };
        console.log(editVal);

        const res = await ExpenseService.editExpense(editVal);

        if (res?.status) {
          notify.success(res.message);

          setExpenses((prev) =>
            prev.map((e) =>
              e.id === editingExpense.id ? { ...e, ...expenseData } : e,
            ),
          );

          getAllExpeseData();

          messageApi.success("Expense created!");
        } else {
          notify.error(res?.message || "Failed to create expense");
          getAllExpeseData();
        }

        getAllExpeseData();

        messageApi.success("Expense updated!");
      } else {
        const req = { ...expenseData };

        console.log("req", req);

        const res = await ExpenseService.createExpense(req);

        if (res?.status) {
          notify.success(res.message);

          // Only update UI if API succeeds
          setExpenses((prev) => [
            ...prev,
            { ...expenseData, id: res.data?.id || Date.now() },
          ]);
          getAllExpeseData();

          messageApi.success("Expense created!");
        } else {
          notify.error(res?.message || "Failed to create expense");
          getAllExpeseData();
        }
      }

      // Optional
      // setDrawerOpen(false);
      // form.resetFields();
    } catch (error) {
      console.error("Save Error:", error);
      messageApi.error("Validation failed or something went wrong");
    }
  };

  const handleAddItem = async () => {
    try {
      const vals = await itemForm.validateFields();

      const amount = Number(vals.qty || 0) * Number(vals.rate || 0);

      const itemData = {
        ...vals,
        is_stock: isStock,
        amount,
        updated_at: formatDate(),
      };

      if (editItemIdx !== null) {
        setItems((prev) =>
          prev.map((item, index) =>
            index === editItemIdx
              ? {
                ...item,
                ...itemData,
              }
              : item
          )
        );

        setEditItemIdx(null);
        notify.success("Item updated successfully");
      } else {
        setItems((prev) => [
          ...prev,
          {
            ...itemData,
            created_at: formatDate(),
          },
        ]);

        notify.success("Item added successfully");
      }

      itemForm.resetFields();
      setIsStock(false);
      setAddItemModal(false);
    } catch (error) {
      notify.warning("Please fill all required fields");
    }
  };

  const itemColumns = [
    { title: "Description", dataIndex: "name", key: "name" },
    {
      title: "Add to Stock",
      dataIndex: "is_stock",
      align: 'center',
      width: 130,
      render: (value) =>
        value ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },

    { title: "Unit", dataIndex: "unit", key: "unit", width: 70 },
    { title: "Qty", dataIndex: "qty", key: "qty", width: 70 },

    {
      title: "Rate (₹)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (v) => `₹${v?.toLocaleString()}`,
    },
    {
      title: "Amount (₹)",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (v) => <Text strong>₹{v?.toLocaleString()}</Text>,
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_, row, idx) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditItemIdx(idx);
              itemForm.setFieldsValue(row);
              setAddItemModal(true);
              setIsStock(!!row.is_stock);
            }}
          />
          <Popconfirm
            title="Remove item?"
            onConfirm={() => handleDeleteItem(row, idx)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleVendorUpdate = (options, values) => {
    console.log(values);
    setSelectedVendor(values);
  };
  const handelCatUpdate = (options, values) => {
    console.log(values);
    setSelectedCate(values);
  };

  const expandedRowRender = (record) => (
    <div
      style={{ padding: "12px 24px", background: "#fafafa", borderRadius: 8 }}
    >
      <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
        <Col span={6}>
          <Text type="secondary">Vendor:</Text>{" "}
          <Text strong>{record.vendor_name}</Text>
        </Col>
        <Col span={6}>
          <Text type="secondary">Category:</Text>{" "}
          <Tag color={CATEGORY_COLORS[record.category_name]}>
            {record.category_name}
          </Tag>
        </Col>

        <Col span={6}>
          <Text type="secondary">Paid By:</Text>{" "}
          <Text strong>{record.paid_by || "—"}</Text>
        </Col>
        <Col span={6}>
          <Text type="secondary">Paid Date:</Text>{" "}
          <Text strong>{record.paid_date || "—"}</Text>
        </Col>
        <Col span={24}>
          <Text type="secondary">Description:</Text>{" "}
          <Text> {record.description}</Text>
        </Col>
      </Row>
      <Table
        size="small"
        columns={[
          { title: "Item Description", dataIndex: "name" },
          { title: "Unit", dataIndex: "unit", width: 80 },
          { title: "Qty", dataIndex: "qty", width: 70 },
          {
            title: "Rate",
            dataIndex: "rate",
            width: 100,
            render: (v) => `₹${v?.toLocaleString()}`,
          },
          {
            title: "Amount",
            dataIndex: "amount",
            width: 120,
            render: (v) => (
              <Text strong style={{ color: "#1677ff" }}>
                ₹{v?.toLocaleString()}
              </Text>
            ),
          },
        ]}
        dataSource={record.items}
        pagination={false}
        bordered
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell colSpan={3}>
              <Text strong>Total</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              <Text strong style={{ color: "#52c41a", fontSize: 15 }}>
                ₹{record.total_amount?.toLocaleString()}
              </Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );

  const mainColumns = [
    {
      title: "Bill No",
      dataIndex: "bill_no",
      key: "bill_no",
      width: 110,
      render: (v) => (
        <Tag style={{ fontWeight: 700, letterSpacing: 0.5 }} color="blue">
          {v}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      width: 140,
    },
    { title: "Vendor", dataIndex: "vendor_name", key: "vendor_name" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 130,
      render: (v) => (
        <Text strong style={{ color: "#1677ff", fontSize: 15 }}>
          ₹{v?.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => (
        <Tooltip title="Click to toggle status">
          <Tag
            icon={
              status === "paid" ? (
                <CheckCircleOutlined />
              ) : (
                <ClockCircleOutlined />
              )
            }
            color={status === "paid" ? "success" : "warning"}
            style={{ cursor: "pointer", fontWeight: 600 }}
            onClick={() => handleStatusToggle(record.id)}
          >
            {status.toUpperCase()}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: "Paid", value: "paid" },
        { text: "Unpaid", value: "unpaid" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (_, record) => (
        <Space>
          <Tooltip title="Expand Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setExpandedRows((prev) =>
                  prev.includes(record.id)
                    ? prev.filter((k) => k !== record.id)
                    : [...prev, record.id],
                );
              }}
            />
          </Tooltip>
          {canUpdateModule(MODULE_KEY) && (
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                type="primary"
                ghost
                onClick={() => openEdit(record)}
              />
            </Tooltip>
          )}

          {canDeleteModule(MODULE_KEY) && (
            <Popconfirm
              title="Delete this expense?"
              icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
              onConfirm={() => handleDelete(record.id)}
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canViewModule(MODULE_KEY)) {
    return <Empty description="No permission to view this page" />;
  }

  return (
    <>
      <Card
        title={
          <div style={{ margin: 14 }}>
            <Title level={3} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Expenses Tracker
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Track spending patterns, categorize expenses & monitor financial activity overview
            </Text>
          </div>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                setFilters({
                  search: "",
                  status: "all",
                  category: "all",
                  dateRange: null,
                })
              }
            >
              Reload
            </Button>
            {/* <Button icon={<DownloadOutlined />}>Export</Button> */}
            {canCreateModule(MODULE_KEY) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="middle"
                onClick={openCreate}
                style={{ background: "#1677ff", fontWeight: 600 }}
              >
                Add Expense
              </Button>
            )}
          </Space>
        }
      >
        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            {
              title: "Total Expenses",
              value: stats.total,
              icon: <DollarOutlined />,
              color: "#1677ff",
              bg: "#e6f4ff",
            },
            {
              title: "Total Bills",
              value: stats.count,
              prefix: "",
              suffix: " bills",
              icon: <FileTextOutlined />,
              color: "#722ed1",
              bg: "#f9f0ff",
            },
            {
              title: "Paid Amount",
              value: stats.paid,
              icon: <CheckCircleOutlined />,
              color: "#52c41a",
              bg: "#f6ffed",
            },
            {
              title: "Unpaid Amount",
              value: stats.unpaid,
              icon: <ClockCircleOutlined />,
              color: "#ff4d4f",
              bg: "#fff1f0",
            },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: s.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                      color: s.color,
                    }}
                  >
                    {s.icon}
                  </div>
                  <Statistic
                    title={
                      <span style={{ fontSize: 12, color: "#8c9cb8" }}>
                        {s.title}
                      </span>
                    }
                    value={s.title === "Total Bills" ? s.value : s.value}
                    prefix={s.title !== "Total Bills" ? "₹" : ""}
                    suffix={s.suffix || ""}
                    valueStyle={{
                      color: s.color,
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                  />
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <Card
          bordered={false}
          style={{ marginBottom: 20, borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <Input
                prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                placeholder="Search bill, vendor_name, description..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                allowClear
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                value={filters.status}
                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                style={{ width: "100%" }}
              >
                <Option value="all">All Status</Option>
                <Option value="paid">
                  <CheckCircleOutlined style={{ color: "#52c41a" }} /> Paid
                </Option>
                <Option value="unpaid">
                  <ClockCircleOutlined style={{ color: "#faad14" }} /> Unpaid
                </Option>
              </Select>
            </Col>

            <Col xs={12} sm={6} md={4}>
              <Select
                value={filters.category}
                placeholder="Select category"
                style={{ width: "100%" }}
                showSearch
                allowClear
                onChange={(v, r) => {
                  console.log("Selected category:", r?.label);
                  setFilters((f) => ({ ...f, category: r?.label }));
                }}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label?.toLowerCase() || "";
                  const value = option?.value?.toString().toLowerCase() || "";
                  return (
                    label.includes(input.toLowerCase()) ||
                    value.includes(input.toLowerCase())
                  );
                }}
                options={[{ label: "all", value: "all" }, ...cat]}
              />
            </Col>

            <Col xs={24} sm={12} md={7}>
              {/* <RangePicker
                style={{ width: "100%" }}
                value={filters.dateRange}
                onChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
                placeholder={["From Date", "To Date"]}
              /> */}
              <RangePicker
  style={{ width: "100%" }}
  format="DD-MM-YYYY"
  value={filters.dateRange}
  onChange={(v) =>
    setFilters((f) => ({
      ...f,
      dateRange: v,
    }))
  }
  placeholder={["From Date", "To Date"]}
/>
            </Col>
            <Col xs={24} sm={6} md={3}>
              <Button onClick={() => setFilters({
                search: "",
                status: "all",
                category: "all",
                dateRange: null,
              })} icon={<FilterOutlined />} block>Clear</Button>
            </Col>
          </Row>
        </Card>

        {/* Date-wise expense groups */}
        {Object.keys(groupedByDate).length === 0 ? (
          <Card
            bordered={false}
            style={{ borderRadius: 12, textAlign: "center", padding: "40px 0" }}
          >
            <Empty description="No expenses found" />
          </Card>
        ) : (
          Object.entries(groupedByDate).map(([expense_date, exps]) => {
            const dayTotal = exps.reduce(
              (s, e) => s + Number(e.total_amount || 0),
              0,
            );
            const paidCount = exps.filter((e) => e.status === "paid").length;
            return (
              <div key={expense_date} style={{ marginBottom: 20 }}>
                {/* Date header */}
                <Alert
                  type="info"
                  style={{
                    marginBottom: 1,
                    borderRadius: 1,
                  }}
                  description={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Space>
                        <CalendarOutlined
                          style={{ color: "#faad14", fontSize: 16 }}
                        />
                        <Text strong style={{ color: "#000", fontSize: 14 }}>
                          {dayjs(expense_date).format("dddd, DD MMMM YYYY")}
                        </Text>
                        <Tag color="gold" style={{ marginLeft: 8 }}>
                          {exps.length} bills
                        </Tag>
                      </Space>

                      <Space>
                        <Tag color="green">{paidCount} paid</Tag>
                        <Tag color="orange">{exps.length - paidCount} unpaid</Tag>
                        <Text strong style={{ color: "#000000ff", fontSize: 15 }}>
                          ₹{dayTotal.toLocaleString()}
                        </Text>
                      </Space>
                    </div>
                  }
                />
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(255, 0, 0, 0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Table
                    rowKey="id"
                    columns={mainColumns}
                    dataSource={exps}
                    pagination={false}
                    size="middle"
                    expandable={{
                      expandedRowKeys: expandedRows,
                      expandedRowRender,
                      onExpand: (expanded, record) => {
                        setExpandedRows((prev) =>
                          expanded
                            ? [...prev, record.id]
                            : prev.filter((k) => k !== record.id),
                        );
                      },
                      showExpandColumn: false,
                    }}
                    rowClassName={(record) =>
                      record.status === "unpaid" ? "unpaid-row" : ""
                    }
                    summary={(pageData) => {
                      const total = pageData.reduce(
                        (s, r) => s + Number(r.total_amount),
                        0,
                      );
                      return (
                        <Table.Summary.Row style={{ background: "#f9fafb" }}>
                          <Table.Summary.Cell colSpan={4}>
                            <Text strong>Day Total</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text
                              strong
                              style={{ color: "#52c41a", fontSize: 15 }}
                            >
                              ₹{Number(total || 0).toLocaleString()}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell colSpan={2} />
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </Card>
              </div>
            );
          })
        )}
      </Card>

      {/* Create/Edit Drawer */}
      <Drawer
        title={
          <Space>
            {editingExpense ? (
              <EditOutlined style={{ color: "#1677ff" }} />
            ) : (
              <PlusOutlined style={{ color: "#52c41a" }} />
            )}
            <span>
              {editingExpense
                ? `Edit ${editingExpense.bill_no}`
                : "New Expense"}
            </span>
          </Space>
        }
        width={880}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <Space
            size="large"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>

            <Button
              type="primary"
              onClick={handleSave}
              icon={editingExpense ? <EditOutlined /> : <PlusOutlined />}
            >
              {editingExpense ? "Update" : "Create"} Expense
            </Button>
          </Space>
        }

        extra={<Button
          type="primary"
          variant="filled"
          onClick={() => setOpen(true)}
          size="middle"
        >
          Add Vendor
        </Button>}
      // bodyStyle={{ paddingBottom: 40 }}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Basic Info
            </Text>

          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expense_date"
                label="Expense Date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }}   format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="Select category"
                  options={cat}
                  showSearch
                  onChange={handelCatUpdate}
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const label = option?.label?.toLowerCase() || "";
                    const value = option?.value?.toString().toLowerCase() || "";
                    return (
                      label.includes(input.toLowerCase()) ||
                      value.includes(input.toLowerCase())
                    );
                  }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vendor_id"
                label="Vendor / Supplier"
                rules={[{ required: true }]}
              >
                <Select
                  allowClear
                  placeholder="Select Vendor"
                  options={vendors}
                  onChange={handleVendorUpdate}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const label = option?.label?.toLowerCase() || "";
                    const value = option?.value?.toString().toLowerCase() || "";
                    return (
                      label.includes(input.toLowerCase()) ||
                      value.includes(input.toLowerCase())
                    );
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Payment Status"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="paid">
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> Paid
                  </Option>
                  <Option value="unpaid">
                    <ClockCircleOutlined style={{ color: "#faad14" }} /> Unpaid
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true }]}
              >
                <TextArea rows={2} placeholder="Brief description of expense" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.status !== curr.status}
          >
            {({ getFieldValue }) =>
              getFieldValue("status") === "paid" && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="paid_date" label="Paid Date">
                      <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="paid_by" label="Paid By">
                      <Input placeholder="Name of person who paid" />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Expense Items
            </Text>
          </Divider>

          <div
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text type="secondary">Add line items for detailed tracking</Text>
            <Button
              size="middle"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => {
                itemForm.resetFields();
                setEditItemIdx(null);
                setAddItemModal(true);
              }}
            >
              Add Item
            </Button>
          </div>

          {items.length > 0 ? (
            <Table
              size="small"
              columns={itemColumns}
              dataSource={items}
              pagination={false}
              bordered
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={3}>
                    <Text strong>Total Amount</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <Text strong style={{ color: "#52c41a" }}>
                      ₹
                      {items
                        .reduce((s, i) => s + (Number(i.amount) || 0), 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell />
                </Table.Summary.Row>
              )}
            />
          ) : (
            <div
              style={{
                background: "#fafafa",
                border: "1px dashed #d9d9d9",
                borderRadius: 8,
                padding: "20px",
                textAlign: "center",
                marginBottom: 16,
              }}
            >
              <Text type="secondary">
                No items added. Add items or enter a manual amount below.
              </Text>
            </div>
          )}

          {items.length === 0 && (
            <Form.Item
              name="amount"
              label="Total Amount (₹)"
              rules={[{ required: true }]}
              style={{ marginTop: 16 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                prefix="₹"
                placeholder="Enter total amount"
                min={0}
              />
            </Form.Item>
          )}
        </Form>
      </Drawer>

      {/* Add Item Modal */}
      <Modal
        title={editItemIdx !== null ? "Edit Item" : "Add Expense Item"}
        open={addItemModal}
        onCancel={() => {
          setAddItemModal(false);
          setEditItemIdx(null);
          setIsStock(false);
          itemForm.resetFields();
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Checkbox
              checked={isStock}
              onChange={(e) => setIsStock(e.target.checked)}
            >
              Add to Stock
            </Checkbox>

            <Space>
              <Button
                onClick={() => {
                  setAddItemModal(false);
                  setEditItemIdx(null);
                  setIsStock(false);
                  itemForm.resetFields();
                }}
              >
                Cancel
              </Button>

              <Button type="primary" onClick={handleAddItem}>
                {editItemIdx !== null ? "Update" : "Add"}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={itemForm} layout="vertical">
          <Form.Item
            name="name"
            label="Item Description"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. Electricity units consumed" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="qty"
                label="Quantity"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="unit" label="Unit">
                <Input style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="rate"
                label="Rate (₹)"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} prefix="₹" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {() => {
              const qty = itemForm.getFieldValue("qty") || 0;
              const rate = itemForm.getFieldValue("rate") || 0;
              return qty && rate ? (
                <div
                  style={{
                    background: "#f6ffed",
                    border: "1px solid #b7eb8f",
                    borderRadius: 6,
                    padding: "8px 12px",
                  }}
                >
                  <Text>
                    Line Total:{" "}
                    <Text strong style={{ color: "#52c41a" }}>
                      ₹{(qty * rate).toLocaleString()}
                    </Text>
                  </Text>
                </div>
              ) : null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      <CreateVendorModal
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={getAllVendor}
      />

      <style>{`
        .unpaid-row { background: #fffbe6 !important; }
        .unpaid-row:hover > td { background: #fff7cc !important; }
        .ant-table-row:hover > td { transition: background 0.2s; }
      `}</style>
    </>
  );
}
